import React, { useEffect, useState } from "react";
import { Wrench } from "lucide-react";
import {
  createStripeAccountLink,
  addUserAsProviderManager,
  inviteUserAsProviderResource,
  getProviderManagerAndResource,
  getProviderManagerAndResourceDetails,
} from "../../../../services/staffingService";
import {
  unlinkCalendar,
  nylasOAuth,
  zendeskOAuth,
  unlinkZendesk,
} from "../../../../services/staffingService";

import { Redirect, Link, useParams } from "react-router-dom";
import DashboardSidebar from "../agencyDashboardSidebar";
import { useUser } from "../../../../context/UserContext";
import { useProviderCompany } from "../../../../context/ProviderCompanyContext";

import GoogleCalendar from "../../icons/google-calendar.png";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";


const providerManagerSchema = yup.object({
  providerManagerEmail: yup
    .string()
    .required("Email is required for Provider Manager")
    .email("Must be a valid email address"),
});

const providerResourceSchema = yup.object({
  providerResourceEmail: yup
    .string()
    .required("Email is required for Provider Resource")
    .email("Must be a valid email address"),
});

const Account = () => {
  const { userInfo, userInfoReady } = useUser();

  const { providerCompanyId } = useParams();
  const { providerCompanyData, fetchProviderCompanyData } =
    useProviderCompany();
  const [loading, setLoading] = useState(true);
  const [showSideDrawer, setShowSideDrawer] = useState(false);
  const [managerResponse, setManagerResponse] = useState("");
  const [managerError, setManagerError] = useState(false);

  const [resourceResponse, setResourceResponse] = useState("");
  const [resourceError, setResourceError] = useState(false);

  const [providerManagers, setProviderManagers] = useState(0);
  const [providerResources, setProviderResources] = useState(0);

  const tokenIsValid = isTokenValid(userInfo.integrations);
  const expiryDate = getTokenExpiryDate(userInfo.integrations);

  const [progress, setProgress] = useState(0);

  const getProviderManagerAndResourceDetailsData = async () => {
    const resp = await getProviderManagerAndResourceDetails(providerCompanyId);
    setProviderManagers(resp.data.providerManagers);
    setProviderResources(resp.data.providerResources);
  };

  const {
    register: registerManager,
    handleSubmit: handleSubmitManager,
    formState: { errors: errorsManager },
    reset: resetManager,
  } = useForm({
    resolver: yupResolver(providerManagerSchema),
  });

  const {
    register: registerResource,
    handleSubmit: handleSubmitResource,
    formState: { errors: errorsResource },
    reset: resetResource,
  } = useForm({
    resolver: yupResolver(providerResourceSchema),
  });

  const onSubmitProviderManager = async (data) => {
    try {
      const resp = await addUserAsProviderManager(
        providerCompanyId,
        data.providerManagerEmail
      );
      setManagerResponse(resp.data);
      setManagerError(false);
    } catch (error) {
      setResourceResponse(error.response.data || "Failed to send invitation");
      setResourceError(true);
    }
  };

  const onSubmitProviderResource = async (data) => {
    try {
      const resp = await inviteUserAsProviderResource(
        providerCompanyId,
        data.providerResourceEmail
      );
      setResourceResponse(resp.data);
      setResourceError(false);
    } catch (error) {
      setResourceResponse(error.response.data || "Failed to send invitation");
      setResourceError(true);
    }
  };

  useEffect(() => {
    if (providerCompanyId) {
      fetchProviderCompanyData(providerCompanyId);
      getProviderManagerAndResourceDetailsData();
      // setProgress(calculateProgress(providerCompanyData, totalProviderManagers, totalProviderResources ));
      setLoading(false);
    }
  }, [providerCompanyId, fetchProviderCompanyData]);



  const calculateProgress = (
    providerCompanyData,
    totalProviderManagers,
    totalProviderResources
  ) => {
    let progress = 20;
    return progress;
  };

  const handleLinkCalendar = async () => {
    const resp = await nylasOAuth();
    window.location.href = resp.data.authUrl;
  };

  const handleUnlinkCalendar = async (grantId) => {
    await unlinkCalendar(grantId);
    // refresh page?
    // fetchUser() ;
  };

  function isTokenValid(integrations) {
    if (!integrations || !integrations.nylas) {
      return false;
    }
    const { expiresIn, created } = integrations.nylas;
    const issueDate = new Date(created);
    const expiryDate = new Date(issueDate.getTime() + expiresIn * 1000);
    const now = new Date();

    return now < expiryDate;
  }

  function getTokenExpiryDate(integrations) {
    if (!integrations || !integrations.nylas) {
      return null;
    }

    const { expiresIn, created } = integrations.nylas;
    const issueDate = new Date(created);
    const expiryDate = new Date(issueDate.getTime() + expiresIn * 1000); // expiresIn is in seconds
    return expiryDate.toLocaleString(); // Formats date and time to local string
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-inter">
      <DashboardSidebar isOpen={showSideDrawer} setIsOpen={setShowSideDrawer} />
      <main className="flex-1 overflow-hidden flex flex-col">

        {/* ── Page header (sticky) ───────────────────────────── */}
        <div className="border-b border-gray-200 bg-white px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Wrench size={20} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Account Settings</h1>
              <p className="text-sm text-gray-500">Manage your account integrations and configurations.</p>
            </div>
          </div>
        </div>

        {/* ── Scrollable content ──────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6">
              {/* <div className="staff-assignments-cards-container"></div> */}
              {providerCompanyData && (
                <>
                  <div>

                    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-50 flex-shrink-0">
                          <Wrench size={20} className="text-teal-500" />
                        </div>
                        <h6 className="text-sm font-semibold text-brand-blue-dark">
                          Integrations
                        </h6>
                      </div>

                      <div className="flex items-center ml-8">
                        <img
                          className="w-10 h-10 mr-4"
                          src={GoogleCalendar}
                          alt="Google Calendar"
                        />
                        <div className="flex flex-col">
                          <p className="text-sm font-medium text-gray-800 mb-1">
                            Google Calendar
                          </p>
                          {userInfo.integrations &&
                          userInfo.integrations.nylas ? (
                            <p className="text-xs text-gray-600">
                              {userInfo.integrations.nylas.email} <br />
                              {tokenIsValid ? (
                                <>
                                  <span
                                    className="text-xs text-teal-600 cursor-pointer underline"
                                    onClick={() =>
                                      handleUnlinkCalendar(
                                        userInfo.integrations.nylas.grantId
                                      )
                                    }
                                  >
                                    (Unlink)
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span
                                    className="text-xs text-teal-600 cursor-pointer underline"
                                    onClick={handleLinkCalendar}
                                  >
                                    (Relink)
                                  </span>
                                  <span> - Token expired</span>
                                </>
                              )}
                            </p>
                          ) : (
                            <p
                              className="text-xs text-teal-600 cursor-pointer underline"
                              onClick={handleLinkCalendar}
                            >
                              Link your calendar
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {loading && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                  <div className="w-12 h-12 border-4 border-t-primary rounded-full animate-spin"></div>
                </div>
              )}
        </div>
      </main>
    </div>
  );
};
export default Account;
